/**
 * Shared city search module.
 * Used by both the hero search (homepage) and the header search (navigation bar).
 *
 * Usage:
 *   initCitySearch({
 *     inputSelector: '#my-input',
 *     resultsSelector: '#my-results',
 *     containerSelector: '.my-container'
 *   });
 */
(function () {
  'use strict';

  var GOOGLE_FORM_URL =
    'https://docs.google.com/forms/d/e/1FAIpQLSdZ7Al-uMZsDSRfYs0lNUJWP_4P5UbbNuyXorkvKBy0zOK0qQ/formResponse';
  var ENTRY_SEARCH_TERM = 'entry.1594810122';

  /**
   * Normalise a string for comparison (lowercase, no diacritics, articles
   * stripped, hyphens/apostrophes treated as spaces). Lets "Mont Saint-Michel"
   * (Shopify collection) match "Le Mont-Saint-Michel" (INSEE commune name).
   */
  function normalise(str) {
    return str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/^(le |la |les |l')/, '')
      .replace(/[\s\-']+/g, ' ')
      .trim();
  }

  /**
   * Initialise a city search instance on the given elements.
   * Can be called multiple times for different search inputs on the same page.
   */
  function initCitySearch(config) {
    var input = document.querySelector(config.inputSelector);
    var resultsContainer = document.querySelector(config.resultsSelector);
    var container = document.querySelector(config.containerSelector);

    if (!input || !resultsContainer) return;

    var searchTypingTimer;
    var googleFormTimer;

    var shopifyRoot =
      window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';

    /* ---- helpers ---- */

    function isPostalCode(query) {
      return /^\d{2,5}$/.test(query.trim());
    }

    /**
     * Query Shopify's predictive search for collections matching `query`.
     * Always resolves to an array (empty on error).
     */
    function searchCollections(query, limit) {
      return fetch(
        shopifyRoot +
          'search/suggest.json?q=' +
          encodeURIComponent(query) +
          '&resources[type]=collection&resources[limit]=' +
          (limit || 8)
      )
        .then(function (r) {
          return r.json();
        })
        .then(function (data) {
          return data.resources.results.collections || [];
        })
        .catch(function () {
          return [];
        });
    }

    function sendToGoogleForm(term) {
      var formData = new FormData();
      formData.append(ENTRY_SEARCH_TERM, term);
      fetch(GOOGLE_FORM_URL, {
        method: 'POST',
        mode: 'no-cors',
        body: formData,
      })
        .then(function () {
          console.log('Search logged to Google Form: ' + term);
        })
        .catch(function (err) {
          console.error('Error logging search', err);
        });
    }

    /* ---- rendering ---- */

    function renderResults(collections, communes, fromPostalCode) {
      var html = '';

      if (fromPostalCode) {
        html +=
          '<div class="city-search-postal-badge">\uD83D\uDCEE Résultats par code postal</div>';
      }

      // Render Shopify collections first
      collections.forEach(function (item) {
        var imgTag = '';
        if (item.featured_image && item.featured_image.url) {
          imgTag =
            '<img src="' +
            item.featured_image.url +
            '" class="city-search-result-image" alt="' +
            (item.alt || '') +
            '">';
        } else {
          imgTag =
            '<div class="city-search-result-image city-search-result-image--empty"></div>';
        }

        html +=
          '<a href="' +
          item.url +
          '" class="city-search-result-item">' +
          imgTag +
          '<div class="city-search-result-info">' +
          '<span class="city-search-result-title">' +
          item.title +
          '</span>' +
          '</div>' +
          '</a>';
      });

      // Render communes without collections (subtle, not too different)
      communes.forEach(function (commune) {
        var communeUrl =
          '/pages/ville-bientot-disponible?ville=' +
          encodeURIComponent(commune.nom);

        html +=
          '<a href="' +
          communeUrl +
          '" class="city-search-result-item city-search-result-item--coming-soon">' +
          '<div class="city-search-result-image city-search-result-image--placeholder">' +
          '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#999" stroke-width="1.5"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>' +
          '</div>' +
          '<div class="city-search-result-info">' +
          '<span class="city-search-result-title">' +
          commune.nom +
          '</span>' +
          '</div>' +
          '</a>';
      });

      if (collections.length === 0 && communes.length === 0) {
        html =
          '<div class="city-search-message">Aucune commune trouvée.</div>';
      }

      resultsContainer.innerHTML = html;
      resultsContainer.style.display = 'block';
    }

    /* ---- fetching ---- */

    function fetchCollectionsAndCommunes(query) {
      // Launch both searches in parallel
      var collectionsPromise = searchCollections(query, 8);

      var communesPromise = fetch(
        'https://geo.api.gouv.fr/communes?nom=' +
          encodeURIComponent(query) +
          '&fields=nom,codesPostaux&boost=population&limit=10'
      )
        .then(function (r) {
          return r.json();
        })
        .catch(function () {
          return [];
        });

      Promise.all([collectionsPromise, communesPromise]).then(function (
        results
      ) {
        var collections = results[0];
        var allCommunes = results[1] || [];

        // Build a set of normalised collection titles to filter out matching communes
        var collectionTitles = new Set();
        collections.forEach(function (c) {
          collectionTitles.add(normalise(c.title));
        });

        // Communes not already covered by the first collection search
        var unmatchedCommunes = allCommunes.filter(function (commune) {
          return !collectionTitles.has(normalise(commune.nom));
        });

        if (unmatchedCommunes.length === 0) {
          renderResults(collections, unmatchedCommunes, false);
          return;
        }

        // Shopify's predictive search misses some collections when the typed
        // query omits a leading article/apostrophe — e.g. "isle-sur-la-sorgue"
        // does not match the collection titled "L'Isle-sur-la-Sorgue", but
        // "l'isle-sur-la-sorgue" does. The geo API always returns the
        // canonical commune name *with* its article, so re-query Shopify with
        // that name to recover the collection when the first search missed it.
        var recheckPromises = unmatchedCommunes.map(function (commune) {
          return searchCollections(commune.nom, 3);
        });

        Promise.all(recheckPromises).then(function (recheckResults) {
          var seenUrls = new Set();
          collections.forEach(function (c) {
            seenUrls.add(c.url);
          });

          var stillUnmatched = [];

          unmatchedCommunes.forEach(function (commune, i) {
            var matches = recheckResults[i].filter(function (c) {
              return normalise(c.title) === normalise(commune.nom);
            });

            if (matches.length === 0) {
              stillUnmatched.push(commune);
              return;
            }

            matches.forEach(function (c) {
              if (!seenUrls.has(c.url)) {
                seenUrls.add(c.url);
                collections.push(c);
              }
            });
          });

          renderResults(collections, stillUnmatched, false);
        });
      });
    }

    function fetchByPostalCode(postalCode) {
      resultsContainer.style.display = 'block';
      resultsContainer.innerHTML =
        '<div class="city-search-message">\uD83D\uDD0D Recherche par code postal...</div>';

      fetch(
        'https://geo.api.gouv.fr/communes?codePostal=' +
          postalCode +
          '&fields=nom,codesPostaux&boost=population&limit=10'
      )
        .then(function (response) {
          return response.json();
        })
        .then(function (communes) {
          if (!communes || communes.length === 0) {
            resultsContainer.innerHTML =
              '<div class="city-search-message">Aucune commune trouvée pour ce code postal.</div>';
            return;
          }

          // Search collections for each commune name
          var searchPromises = communes.map(function (commune) {
            return searchCollections(commune.nom, 3).then(function (
              collections
            ) {
              return collections.map(function (c) {
                return Object.assign({}, c, {
                  _communeName: commune.nom,
                  _postalCode: postalCode,
                });
              });
            });
          });

          return Promise.all(searchPromises).then(function (results) {
            var allCollections = results.flat();
            var seen = new Set();
            var uniqueCollections = allCollections.filter(function (c) {
              if (seen.has(c.url)) return false;
              seen.add(c.url);
              return true;
            });

            // Build set of matched commune names
            var matchedNames = new Set();
            uniqueCollections.forEach(function (c) {
              matchedNames.add(normalise(c.title));
            });

            // Communes without a matching collection
            var unmatchedCommunes = communes.filter(function (commune) {
              return !matchedNames.has(normalise(commune.nom));
            });

            renderResults(uniqueCollections, unmatchedCommunes, true);
          });
        })
        .catch(function (err) {
          console.error('Error fetching postal code:', err);
          resultsContainer.style.display = 'block';
          resultsContainer.innerHTML =
            '<div class="city-search-message">Erreur lors de la recherche. Réessayez.</div>';
        });
    }

    /* ---- event listeners ---- */

    input.addEventListener('input', function () {
      var query = this.value.trim();

      clearTimeout(searchTypingTimer);
      clearTimeout(googleFormTimer);

      if (query.length < 2) {
        resultsContainer.style.display = 'none';
        resultsContainer.innerHTML = '';
        return;
      }

      // Google Form logging (1s debounce)
      if (query.length > 2) {
        googleFormTimer = setTimeout(function () {
          sendToGoogleForm(query);
        }, 1000);
      }

      var delay = isPostalCode(query) ? 500 : 300;

      searchTypingTimer = setTimeout(function () {
        if (isPostalCode(query)) {
          fetchByPostalCode(query);
        } else {
          fetchCollectionsAndCommunes(query);
        }
      }, delay);
    });

    // Close results on click outside
    document.addEventListener('click', function (e) {
      if (container && !container.contains(e.target)) {
        resultsContainer.style.display = 'none';
      }
    });
  }

  // Expose globally
  window.initCitySearch = initCitySearch;
})();
